import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/auth-context";
import { publicApi } from "@/services/api-client";

/** Only safe, non-sensitive account data belongs in the query cache. */
export function useApiResource<T = Record<string, unknown>>(
  path: string | null,
) {
  const { identity } = useAuth();
  const query = useQuery({
    queryKey: ["account", identity?.accountId, "module", path],
    queryFn: ({ signal }) => publicApi<T>(path!, { signal }),
    enabled: Boolean(identity && path),
    retry: false,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
  return {
    data: query.data?.data,
    meta: query.data?.meta,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    reload: query.refetch,
  };
}

export type ApiRecord = Record<string, unknown>;
export function record(value: unknown): ApiRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};
}
export function rows(value: unknown): ApiRecord[] {
  if (Array.isArray(value)) return value.map(record);
  const object = record(value);
  for (const key of [
    "data",
    "items",
    "accounts",
    "members",
    "wallets",
    "documents",
    "applications",
    "webhooks",
    "notifications",
    "cards",
    "customers",
    "users",
    "countries",
    "roles",
    "links",
    "transactions",
    "deliveries",
    "services",
    "rules",
  ]) {
    if (Array.isArray(object[key]))
      return (object[key] as unknown[]).map(record);
  }
  return [];
}
